import {ImageRatioOptions, Image, AboveFoldScrollIndicator} from '../../';
import React, {Fragment, useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';
import root from 'window-or-global';
import throttle from 'lodash.throttle';
import {useSwipeable} from 'react-swipeable';
import {useActiveDetailCard} from '../creatorfeed.hooks';

let delta = 0;
const limit = 10;
let disableDelta = false;

export interface CreatorFeedDetailImagesProps {
    ratio?: ImageRatioOptions;
    detailImages: string[];
}

const Styled = styled.div`
    position: relative;
    padding: 0;
    width: 100%;
    height: 100%;
`;

const Floaty = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    min-height: 100%;
    transition: transform 1s ${(p) => p.theme.easing};
    transform: translateZ(0);
    backface-visibility: hidden;
    perspective: 1000;
    will-change: transform;
`;

const ImageWrap = styled.div`
    padding: 5vh 0;
    &:first-child {
        margin-top: 12vh;
    }
    min-height: 50vh;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    opacity: 0.4;

    &.rect {
        min-height: 48vh;
        &:first-child {
            margin-top: 22vh;
        }
    }

    &.active {
        opacity: 1;
    }
`;

export const CreatorFeedDetailImages = ({
    detailImages = [],
    ratio
}: CreatorFeedDetailImagesProps): JSX.Element => {
    const {activeDetailCard, setActiveDetailCard} = useActiveDetailCard();

    const [translateY, setTranslateY] = useState(0);

    const handleScroll = useCallback(
        (e: WheelEvent) => {
            let newDelta = delta;

            if (disableDelta) return;

            if (activeDetailCard === 0 && e.deltaY < 0) {
                //dont subtract
            } else if (activeDetailCard === detailImages.length - 1 && e.deltaY > 0) {
                //dont add
            } else {
                newDelta += e.deltaY;
            }

            if (newDelta > limit && activeDetailCard < detailImages.length - 1) {
                setActiveDetailCard(activeDetailCard + 1);
                delta = 0;
                disableDelta = true;
                setTimeout(() => {
                    disableDelta = false;
                }, 1000);
            } else if (newDelta < limit && Math.abs(newDelta) > limit && activeDetailCard > 0) {
                setActiveDetailCard(activeDetailCard - 1);
                delta = 0;
                disableDelta = true;
                setTimeout(() => {
                    disableDelta = false;
                }, 1000);
            } else {
                delta = newDelta;
            }
        },
        [activeDetailCard, setActiveDetailCard, detailImages]
    );

    const throttleScroll = throttle(handleScroll, 350, {leading: false});

    useEffect(() => {
        if (activeDetailCard === 0) {
            setTranslateY(0);
            return;
        }

        const activeDetailCardEl = root.document.getElementById(
            `cf-detailimage-${activeDetailCard}`
        );
        if (activeDetailCardEl) {
            const {height} = activeDetailCardEl.getBoundingClientRect();
            setTranslateY(height * activeDetailCard);
        }
    }, [activeDetailCard, setTranslateY]);

    useEffect(() => {
        root.document.addEventListener('wheel', throttleScroll);
        root.document.addEventListener('resize', throttleScroll);
        return () => {
            root.document.removeEventListener('wheel', throttleScroll);
            root.document.removeEventListener('resize', throttleScroll);
        };
    }, [throttleScroll]);

    const handlers = useSwipeable({
        onSwipedUp: () => {
            if (activeDetailCard < detailImages.length - 1) {
                setActiveDetailCard(activeDetailCard + 1);
            }
        },
        onSwipedDown: () => {
            if (activeDetailCard > 0) {
                setActiveDetailCard(activeDetailCard - 1);
            }
        }
    });

    return (
        <Styled>
            <Floaty {...handlers} style={{transform: `translateZ(0) translateY(-${translateY}px)`}}>
                {detailImages.map((src, i) => {
                    if (!src) return null;
                    let active = false;
                    if (i === activeDetailCard) {
                        active = true;
                    }
                    const key = src.replace(/https:\/\/cdn(2|3)\.unrealengine\.com\//, '');

                    return (
                        <Fragment key={`detail-image-wrap-${key}-${i}`}>
                            <ImageWrap
                                className={`${active ? 'active' : ''} ${ratio}`}
                                id={`cf-detailimage-${i}`}>
                                <Image
                                    key={`detail-image-${key}-${i}`}
                                    src={src}
                                    ratio={ratio}
                                    optMaxWidth={800}
                                    noLazy
                                />
                                {i === 0 && activeDetailCard === 0 && detailImages.length > 1 && (
                                    <AboveFoldScrollIndicator />
                                )}
                            </ImageWrap>
                        </Fragment>
                    );
                })}
            </Floaty>
        </Styled>
    );
};
